import { FormikProps } from 'formik';
import React, { useState } from 'react';
import { Grid } from '@mui/material';
import Tooltip from '@cpg-its/ads.common.tooltip';
import Button from '@cpg-its/ads.common.button';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { PensionEnrollmentProps } from '../../../models/arc-epi-props';
import { PensionEnrollmentFormValues } from '../models/pension-enrollment-form-model';
import { BillPayerOption, DB, DCRSVP, PensionEnrollmentPlan, PensionEnrollmentPlanType, PensionEnrollmentStep } from '../models/pension-enrollment-model';
import { setPensionEnrollmentStep } from '../pension-enrollment-slice';
import { tcsmColors } from '../../../../../global/constants/theme/theme-constants/colors';
import { getCurrencyFormat } from '../../../../../global/helpers/format-helpers';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { MonthOptions } from '../../../../../global/constants/lists';
import { TcsmModal } from '../../../../../global/components/tcsm-modal';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { labelSupplementStyles } from '../../../../../global/constants/theme/styles/components/label-supplement-styles';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { adjustForLocalOffset, getCurrentTimeNY } from '../../../../../global/helpers/date-helpers';
import { ExternalLink } from '../../../../../global/components/external-link-modal';


interface Props {
	pensionEnrollmentProps: PensionEnrollmentProps;
	selectedPlan: PensionEnrollmentPlan;
	formProps: FormikProps<PensionEnrollmentFormValues>;
}

export function PensionEnrollmentSelectedPlan(props: Props) {

	const { pensionEnrollmentProps, selectedPlan, formProps } = props;

	const [compensationModalOpen, setCompensationModalOpen] = useState(false);
	const [monthToUse, setMonthToUse] = useState([]);
	const dispatch = useDispatch();
	const history = useHistory();
	const buttonClasses = buttonStyles();
	const labelClasses = labelSupplementStyles();
	const compensationLink = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Compensation}`);
	const enrollmentDate = selectedPlan?.enrollment_date ? adjustForLocalOffset(new Date(selectedPlan?.enrollment_date)) : getCurrentTimeNY();
	let enrollmentEndDate = selectedPlan?.enrollment_date ? adjustForLocalOffset(new Date(selectedPlan?.enrollment_date)) : getCurrentTimeNY();
	enrollmentEndDate?.setMonth(enrollmentDate?.getMonth() + 3);
	const now = new Date();
	if (enrollmentEndDate <= now) {
		now.setMonth(now.getMonth() + 3);
		enrollmentEndDate = now;
	}

	const beginYear = enrollmentDate ? enrollmentDate.getFullYear() : 2018;
	const endYear = enrollmentEndDate.getFullYear();
	const getBeginMonthsToRemove = () => {
		const months = [];
		for (let i = enrollmentDate.getMonth(); i > 0; i -= 1) {
			months.push(i);
		}
		return months;
	};
	const getEndMonthsToRemove = () => {
		const months = [];
		const moreMonth = enrollmentEndDate?.getDate() === 1 ? 2 : 3;
		for (let i = enrollmentEndDate.getMonth() + moreMonth; i <= 12; i += 1) {
			months.push(i);
		}
		return months;
	};
	const beginYearMonthToRemove = getBeginMonthsToRemove();
	const endYearMonthToRemove = getEndMonthsToRemove();
	const monthMap = {};
	const payingPartyOptions = [
		{
			label: pensionEnrollmentProps?.institutionOptionText || 'Institution',
			value: BillPayerOption.Employer,
		},
		{
			label: pensionEnrollmentProps?.dioceseOptionText || 'Diocese',
			value: BillPayerOption.Diocese,
		},
	];

	const getMappedMonth = (year: number) => {
		if (year === beginYear) return MonthOptions?.filter(a => !beginYearMonthToRemove?.includes(a?.value));

		if (year === endYear) return MonthOptions?.filter(a => !endYearMonthToRemove?.includes(a?.value));

		return MonthOptions;

	};
	// gets last 4 years, including the current year
	const yearOptions = (() => {
		const years = [];
		for (let i = beginYear; i <= endYear; i += 1) {
			years.push({
				text: i.toString(),
				value: i,
			});
			monthMap[i] = getMappedMonth(i);
		}
		return years;
	})();

	const handleModalClose = () => {
		setCompensationModalOpen(false);
	};

	const renderPre2018 = () => {
		if (!(selectedPlan?.plan_type === PensionEnrollmentPlanType.DbPlan && selectedPlan?.pbpor_id?.toLowerCase().includes('lay'))) return null;

		return (
			<>
				<p>
					<b>{pensionEnrollmentProps?.pre2018EnrollmentsPrompt}</b>
					<ExternalLink linkUrl={pensionEnrollmentProps?.pre2018EnrollmentsLink} linkText={pensionEnrollmentProps?.pre2018EnrollmentsLinkText} />
				</p>
				<Tooltip iconColor={tcsmColors.blue} title={pensionEnrollmentProps?.pre2018EnrollmentsTooltip} />
			</>
		);
	};

	const assessableCompLabel = selectedPlan?.plan_type === PensionEnrollmentPlanType.DbPlan ? DB : DCRSVP;

	return (
		<DividerSection sectionText={pensionEnrollmentProps.selectedPlanHeader} removeDivider>
			<ContentSpacer>
				<p>{selectedPlan?.pbpor_description}</p>
				<button
					className={buttonClasses.linkButton}
					type='button'
					onClick={() => dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Landing))}>
					{pensionEnrollmentProps?.selectDifferentPlanPrompt}
				</button>
			</ContentSpacer>
			<ContentSpacer>
				<h3 className='divider-section-item-header'>{pensionEnrollmentProps?.billingOrgLabel}</h3>
				<FormField
					name='bill_payer'
					label={pensionEnrollmentProps?.billingOrgLabelDescription}
					component='radioGroup'
					options={payingPartyOptions}
					touched={formProps.touched}
					errors={formProps.errors}
					required
				/>
			</ContentSpacer>
			<ContentSpacer>
				<div className={`${labelClasses.labelLayout} divider-section-item-header`}>
					<h3>{pensionEnrollmentProps?.participationDateLabel}</h3>
					<div className='supplement-wrapper'>
						<Tooltip iconColor={tcsmColors.blue} title={pensionEnrollmentProps?.participationDateTooltip} />
					</div>
				</div>
				<p>{pensionEnrollmentProps?.participationDateLabelDescription}</p>
				<FormGroup>
					<Grid container spacing={1}>
						<Grid item xs={8}>
							<FormField
								name='effective_date_year'
								label={pensionEnrollmentProps?.yearLabel}
								component='input'
								options={yearOptions}
								errors={formProps.errors}
								touched={formProps.touched}
								onBlur={(e) => setMonthToUse(monthMap[e?.target?.value])}
								select
								required
							/>
						</Grid>
					</Grid>
				</FormGroup>
				{

					formProps?.values?.effective_date_year &&
					<>
						<p>{pensionEnrollmentProps?.participationMonthLabelDescription}</p>
						<FormGroup>
							<Grid container spacing={1}>
								<Grid item xs={8}>
									<FormField
										name='effective_date_month'
										label={pensionEnrollmentProps?.monthLabel}
										component='input'
										options={monthToUse}
										errors={formProps.errors}
										touched={formProps.touched}
										select
										required
									/>
								</Grid>
							</Grid>
						</FormGroup>
					</>
				}

				{renderPre2018()}
			</ContentSpacer>
			<div className={labelClasses.labelLayout}>
				<h5>{`${pensionEnrollmentProps?.scheduledTotalAssessableCompLabel} - ${assessableCompLabel}`}</h5>
				<div className='supplement-wrapper'>
					<Tooltip iconColor={tcsmColors.blue} title={pensionEnrollmentProps?.scheduledTotalAssessableCompTooltip} />

				</div>
			</div>
			{selectedPlan?.tac ? getCurrencyFormat(parseFloat(selectedPlan?.tac)) : getCurrencyFormat(0)}
			<button
				className={buttonClasses.linkButton}
				type='button'
				onClick={() => setCompensationModalOpen(true)}>{pensionEnrollmentProps?.updateCompensationPrompt}</button>

			<TcsmModal
				open={compensationModalOpen}
				handleClose={handleModalClose}>
				<>
					<h2>{pensionEnrollmentProps?.compensationModalTitle}</h2>
					<WysiwygWithReactLink content={pensionEnrollmentProps?.compensationModalDescription} />
					<ButtonWrapper flexEnd>
						<Button type='button' buttonStyle='tertiary' text={pensionEnrollmentProps?.cancelButtonText} backgroundColor='#808080' onClick={handleModalClose} />
						<Button type='button' buttonStyle='primary' text={pensionEnrollmentProps?.compensationModalConfirmText} backgroundColor='#0000FF' onClick={() => history.push(compensationLink)} />
					</ButtonWrapper>
				</>

			</TcsmModal>
		</DividerSection>
	);
}